import { defineStore, storeToRefs } from 'pinia'
import { computed, ComputedRef, Ref, ref } from 'vue'
import AuthStoreInterface, { Routes, SettingsMenuItems } from '@/pinia/auth/AuthStoreInterface'
import {
  useAuthService,
  ChangePasswordAndActivatePayload,
  Api_Auth_Login,
  Api_Auth_User_Role,
  Api_Auth_User,
  ConfigResponse,
  useUserService,
} from 'shared-ritm'
import axios from 'axios'
import { calculateRoutes } from '@/components/authorization/helpers/appRouts'
import { initWebsocket, addChanel, deleteListener, addPrivateChanel } from '@/app/providers/websocket'
import { useLayoutStore } from '@/pinia/layout/useLayoutStore'
import { useRouter } from 'vue-router'

export const useAuthStore = defineStore('authMain', (): AuthStoreInterface => {
  const api = useAuthService()
  const userApi = useUserService()
  const { notification, setRootMode } = useLayoutStore()
  const { isRootMode } = storeToRefs(useLayoutStore())
  const eventImport = (id: string) => new CustomEvent('update-task-import', { detail: id })
  const eventImportKtd = (id: string) => new CustomEvent('update-task-import-ktd', { detail: id })
  const router = useRouter()

  const loading: Ref<boolean> = ref(false)
  const _logged: Ref<boolean> = ref(false)
  const _token: Ref<string> = ref('')
  const _userData: Ref<Api_Auth_User> = ref({} as Api_Auth_User)
  const _routes: Ref<Routes[]> = ref([] as any)
  const _roles: Ref<Api_Auth_User_Role[]> = ref([])
  const _accesses: Ref<any> = ref([] as any)
  const _configs: Ref<ConfigResponse> = ref([] as any)
  const _root: Ref<boolean> = ref(false)

  // getters
  const logged = computed(() => _logged.value)

  const token: ComputedRef<string> = computed(() => _token.value)
  const userData: ComputedRef<Api_Auth_User> = computed(() => _userData.value)
  const routes: ComputedRef<any[]> = computed(() => _routes.value)
  const roles: ComputedRef<Api_Auth_User_Role[]> = computed(() => _roles.value)
  const accesses: ComputedRef<any> = computed(() => _accesses.value)
  const configs: ComputedRef<ConfigResponse> = computed(() => _configs.value)

  const userWithMostPermissions = ref({})

  const settingsMenuItems: Ref<SettingsMenuItems[]> = ref([])

  function _updateApiHeaders() {
    const headers = (axios as any).defaults.headers.common
    if (_logged.value) {
      headers['Authorization'] = `Bearer ${_token.value}`
    } else {
      delete headers.Authorization
    }
  }

  function clear() {
    _logged.value = false
    _token.value = ''
    _userData.value = {} as any
    _routes.value = [] as Routes[]
    _roles.value = [] as any[]
    _accesses.value = [] as any[]
    _configs.value = [] as any[]
    _root.value = false
    _saveToStorage()
    //eventEmitter.emit(EmitterEvent.LOGOUT)
  }

  function _saveToStorage() {
    localStorage.setItem('user', JSON.stringify(_userData.value))
    localStorage.setItem('token', _token.value)
    localStorage.setItem('routes', JSON.stringify(_routes.value))
    localStorage.setItem('userRoles', JSON.stringify(_roles.value))
    localStorage.setItem('accesses', JSON.stringify(_accesses.value))
    localStorage.setItem('configs', JSON.stringify(_configs.value))
    localStorage.setItem('root', JSON.stringify(_root.value))
    _updateApiHeaders()
  }

  function updateUser() {
    if (!document.hidden) return

    const ls = localStorage.getItem('user')

    if (!ls || !JSON.parse(ls)?.id) {
      router.push('/sign-in')
    }
  }

  function _loadFromStorage() {
    _token.value = localStorage.getItem('token') || ''
    if (_token.value) {
      _logged.value = true
      _userData.value = JSON.parse(localStorage.getItem('user') || '{}')
      _routes.value = JSON.parse(localStorage.getItem('routes') || '[]')
      _roles.value = JSON.parse(localStorage.getItem('userRoles') || '[]')
      _accesses.value = JSON.parse(localStorage.getItem('accesses') || '[]')
      _configs.value = JSON.parse(localStorage.getItem('configs') || '[]')
      _root.value = JSON.parse(localStorage.getItem('root') || 'false')
      setRootMode(_root.value)
    }
    _updateApiHeaders()
  }

  async function auth(data: Api_Auth_Login) {
    const { user, token } = data
    _logged.value = true
    _token.value = token
    _userData.value = user
    _accesses.value = calculateAccesses(user.roles)
    _routes.value = calculateRoutes(
      _accesses.value,
      user.roles.map(r => r.name),
    )
    userWithMostPermissions.value = user.roles.reduce((maxUser, currentUser) => {
      return currentUser.permissions.length > (maxUser?.permissions?.length || 0) ? currentUser : maxUser
    }, {} as Api_Auth_User_Role)
    _roles.value = user.roles.map(role => ({ ...role, permissions: [] }))
    _userData.value.roles = []
    _userData.value.teams = _userData.value.teams.map(team => {
      const teamRoles = team.roles.map(role => ({ ...role, permissions: [] }))
      return {
        ...team,
        roles: teamRoles,
        permissions: [],
      }
    })
    _saveToStorage()
    const dataConfigs = await api.configs()
    if (dataConfigs && Object.values(dataConfigs).length && dataConfigs?.status !== false) {
      _configs.value = dataConfigs || []
      _saveToStorage()
      return
    }
    if (!dataConfigs.status) {
      throw dataConfigs
    }
  }

  async function updateConfig() {
    await api.configs().then(configs => localStorage.setItem('configs', JSON.stringify(configs)))
  }

  async function authUser(email: string, password: string) {
    //await axios.get('/sanctum/csrf-cookie')
    const data = await api.login(email, password)
    await auth(data)
  }

  async function updateUserData(id: string) {
    //@ts-ignore
    _userData.value = await userApi.getUser(id)
    localStorage.setItem('user', JSON.stringify(_userData.value))
  }

  async function authUserByFace(formData: any) {
    const data = await api.loginVideo(formData)
    await auth(data)
  }

  async function authLogout() {
    await api.logout().then(() => {
      clear()
      window.localStorage.routes = null
      window.location.href = '/sign-in'
      router.push('/sign-in')
    })
  }

  async function authChangePassword(payload: ChangePasswordAndActivatePayload) {
    const { email, currentPassword, password, passwordConfirmation } = payload
    return await api.changePasswordAndActivate(email, currentPassword, password, passwordConfirmation)
  }
  function calculateAccesses(roles: Api_Auth_User_Role[]) {
    const allPermissions: any[] = roles.reduce((acc, { permissions }) => [...acc, ...permissions], [] as any)
    const uniquePermissions: any[] = [
      ...new Map(allPermissions.map((permission: any) => [permission.id, permission])).values(),
    ]
    return uniquePermissions.reduce((acc, { name }) => {
      const [accessKey, accessValue] = name.includes('-')
        ? [name.slice(0, name.lastIndexOf('-')), name.slice(name.lastIndexOf('-') + 1)]
        : [name, null]
      acc[accessKey] = [...(acc[accessKey] || []), accessValue]
      return acc
    }, {})
  }

  const menuItems = computed(() => _routes.value)

  function updateUserHandler(event: any) {
    const user = event?.detail

    if (!user?.id) return

    _userData.value = user
    localStorage.setItem('user', JSON.stringify(user))
  }

  function addEventListeners() {
    window.addEventListener('update-user', updateUserHandler)
    window.addEventListener('storage', updateUser)
  }

  function updateTaskEventCallback(response: any) {
    notification('warning', `${response.message} Текущий статус - "${response.task.status.title}"`)
  }

  function taskImportEndEventCallback(response: any) {
    if (response.message === 'Ошибка импорта') {
      notification('danger', `${response.message}`)
    } else {
      notification('success', `${response.message}`)
      window.dispatchEvent(eventImport(response.project))
    }
  }

  async function ktdImportEventsCallback(response: any) {
    if (response?.error) {
      notification('danger', `${response.message}`)
    } else {
      notification('success', response.message)
      window.dispatchEvent(eventImportKtd(response.project))
    }
  }

  async function ktdParseEventsCallback(response: any) {
    if (response?.error) {
      notification('danger', `${response.message}`)
    } else {
      notification('success', `Для проекта - ${response.project_name}, ${response.title} прошел успешно`)
    }
  }

  function notifyCalcProjectEventCallback(response: any) {
    // await this.fetchNotifications()
    if (response.message.includes('успешно')) {
      notification('success', `Для проекта - ${response.project_name}, ${response.title} прошел успешно`)
    } else {
      notification('danger', `${response.message}`)
    }
  }

  function initWebsocketAndSetChannels() {
    initWebsocket()
    addPrivateChanel(`App.User.${userData.value.id}`, 'TaskImportEndEvent', taskImportEndEventCallback)
    addPrivateChanel(`App.User.${userData.value.id}`, 'ParseKTDMessageEvent', ktdParseEventsCallback)
    addPrivateChanel(`App.User.${userData.value.id}`, 'ImportKTDMessageEvent', ktdImportEventsCallback)
    addPrivateChanel(`App.User.${userData.value.id}`, 'NotifyCalcProjectEvent', notifyCalcProjectEventCallback)
    addChanel('integrations', 'UpdateTaskEvent', updateTaskEventCallback)
  }

  function deleteWebsocket() {
    deleteListener()
    if (userData.value) {
      window.Echo.private('App.User.' + userData.value.id).stopListening('TaskDeadlineEvent')
      window.Echo.private('App.User.' + userData.value.id).stopListening('TaskLinkUpdateEvent')
      window.Echo.private('App.User.' + userData.value.id).stopListening('TaskImportEndEvent')
      window.Echo.private('App.User.' + userData.value.id).stopListening('ParseKTDMessageEvent')
      window.Echo.private('App.User.' + userData.value.id).stopListening('ImportKTDMessageEvent')
      window.Echo.private('App.User.' + userData.value.id).stopListening('NotifyCalcProjectEvent')
      //this.echoWithNamespace.channel('Command').stopListening('NotifyCommandEvent')
    }
  }

  function destroy() {
    window.removeEventListener('update-user', updateUserHandler)
    window.removeEventListener('storage', updateUser)
  }

  function changeVisibleMenuItem(name: string, isShow: boolean) {
    settingsMenuItems.value = settingsMenuItems.value.map(item => (item.name === name ? { ...item, isShow } : item))
  }

  function clickSettingsMenuItem(name: string) {
    if (name === 'instrument-search') router.push('instrument-search')
    if (name === 'equipment-monitoring') window.open('http://10.13.108.11:8000', '_blank')
    if (name === 'root-on') {
      changeVisibleMenuItem('root-on', false)
      changeVisibleMenuItem('root-off', true)
      setRootMode(false)
    }
    if (name === 'root-off') {
      changeVisibleMenuItem('root-on', true)
      changeVisibleMenuItem('root-off', false)
      setRootMode(true)
    }
  }

  function init() {
    _loadFromStorage()
    initWebsocketAndSetChannels()
    addEventListeners()

    const [last_name, first_name] = userData.value?.full_name?.split(' ') || []
    document.title = 'RITM - ' + last_name + ' ' + first_name

    settingsMenuItems.value = [
      { label: 'Поиск инструментов', name: 'instrument-search', isShow: true },
      { label: 'Мониторинг оборудования', name: 'equipment-monitoring', isShow: true },
      {
        label: 'Root-режим выключен',
        name: 'root-off',
        isShow: Array.isArray(accesses.value?.demo) && !isRootMode.value,
      },
      { label: 'Root-режим включен', name: 'root-on', isShow: Array.isArray(accesses.value?.demo) && isRootMode.value },
    ]
  }

  return {
    loading,
    routes,
    configs,
    accesses,
    roles,
    menuItems,
    userWithMostPermissions,
    settingsMenuItems,
    logged,
    token,
    userData,
    authUser,
    updateUserData,
    updateConfig,
    authUserByFace,
    authLogout,
    authChangePassword,
    clear,
    initWebsocketAndSetChannels,
    deleteWebsocket,
    clickSettingsMenuItem,
    init,
    destroy,
  }
})
